import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useSocket } from '../hooks/use-socket'
import { Breadcrumbs, Typography } from '@mui/material'
import { useEffect, useState, useContext, useRef } from 'react'

import { countriesCurrencies } from '../lib'
import Dialog from '../components/common/dialog'
import { resolveAppAccessToken } from '../sso/services/app-access.service'
import { isSuiteSsoRedirectMode } from '../sso/sso-flags'
import { CustomerCompany, FormError } from '../types'
import Pagination from '../components/common/pagination'
import { BankingPartner, Customer } from '../types/user.types'
import { handleSyncDataToLocalDB, normalizeCustomerCompanyData } from '../utils'
import { ActionsBar } from '../components/customer-companies-management/actions-bar'
import { AppStateContext, AuthContext, NotificationsContext, UserContext } from '../contexts'
import { handleDeleteDataByOne, handleGetDataFromTableById } from '../utils/db/common-methods'
import { getCurrentBankingPartners, getCurrentCustomerCompaniesBriefData } from '../api/app-queries.api'
import { deleteCustomerCompanyData, resendCustomerCompanyOnboardingActivationEmail } from '../api/customers.api'
import CustomerCompanyDataListItem from '../components/customer-companies-management/customer-company-data-list-item'
import { AddNewCustomerCompanyModal } from '../components/customer-companies-management/add-new-customer-company-modal'
import { AddNewCustomerCompanyFundingModal } from '../components/customer-companies-management/add-new-customer-company-funding-modal'
import { BankingPartnersPaymentsTestForm } from '../components/customer-companies-management/banking-partners-payments-test-form'

const CustomerCompaniesManagement = () => {
  const navigate = useNavigate()
  const { userSession } = useContext(UserContext)
  const { removeAuth } = useContext(AuthContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { setScrollToTop, updateLoadingStatus } = useContext(AppStateContext)
  const { t } = useTranslation(['customer-companies-management', 'system-users-management', 'common'])

  const currentListPageRef = useRef<number>(1)
  const currentLoadingDataRef = useRef<boolean>(true)
  const customerCompanyToSearchRef = useRef<string>('')
  const customerCompaniesToDeleteRef = useRef<Array<string>>([])
  const currentShowDeleteButtonFromActionsBarRef = useRef<boolean>(false)
  const currentCustomerCompaniesDataRef = useRef<Array<CustomerCompany>>([])
  const bankingPartnersPaymentsTestFormRef = useRef<{ handleSendPayment: () => void }>()

  const [errors, setErrors] = useState<FormError>({})
  const [pageSize, setPageSize] = useState<number>(50)
  const [readyToSend, setReadyToSend] = useState<boolean>(false)
  const [currentListPage, setCurrentListPage] = useState<number>(1)
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [customerData, setCustomerData] = useState<Partial<Customer> | null>(null)
  const [availableCurrencies, setAvailableCurrencies] = useState<Array<string>>([])
  const [customerCompanyToSearch, setCustomerCompanyToSearch] = useState<string>('')
  const [customerCompanyData, setCustomerCompanyData] = useState<CustomerCompany | null>(null)
  const [selectAllCustomerCompanies, setSelectAllCustomerCompanies] = useState<boolean>(false)
  const [customerCompaniesToDelete, setCustomerCompaniesToDelete] = useState<Array<string>>([])
  const [customerCompaniesList, setCustomerCompaniesList] = useState<Array<CustomerCompany>>([])
  const [customerCompaniesData, setCustomerCompaniesData] = useState<Array<CustomerCompany>>([])
  const [openNewCustomerCompanyModal, setOpenNewCustomerCompanyModal] = useState<boolean>(false)
  const [currentBankingPartners, setCurrentBankingPartners] = useState<Array<BankingPartner>>([])
  const [openDeleteCustomerCompanyModal, setOpenDeleteCustomerCompanyModal] = useState<boolean>(false)
  const [showDeleteButtonFromActionsBar, setShowDeleteButtonFromActionsBar] = useState<boolean>(false)
  const [searchedCustomerCompanies, setSearchedCustomerCompanies] = useState<Array<CustomerCompany>>([])
  const [openCreateBankingPartnerTestModal, setOpenCreateBankingPartnerTestModal] = useState<boolean>(false)
  const [openNewCustomerCompanyFundingModal, setOpenNewCustomerCompanyFundingModal] = useState<boolean>(false)
  const [dialogDeleteCustomerCompanyIsLoading, setDialogDeleteCustomerCompanyIsLoading] = useState<boolean>(false)
  const [createBankingPartnerTestModalIsLoading, setCreateBankingPartnerTestModalIsLoading] = useState<boolean>(false)

  const isSuiteDashboardReady = () =>
    !isSuiteSsoRedirectMode() || globalThis.sessionStorage.getItem('suiteDashboardReady') === 'true'

  useEffect(() => {
    if (!userSession || !isSuiteDashboardReady()) {
      return
    }

    handleGetCurrentBankingPartners()
    handleGetCurrentCustomerCompaniesBriefData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  useEffect(() => {
    if (!userSession) {
      return
    }

    const onSuiteDashboardReady = () => {
      handleGetCurrentBankingPartners()
      handleGetCurrentCustomerCompaniesBriefData()
    }

    globalThis.addEventListener('suite-dashboard-ready', onSuiteDashboardReady)
    return () => globalThis.removeEventListener('suite-dashboard-ready', onSuiteDashboardReady)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  useEffect(() => {
    if (!appDataSocket) return

    appDataSocket.on('addNewCustomerCompany', async (dataFromSocket: any) => {
      const customerCompanyDataToAdd = handleGetCustomerCompanyDataFromSocket(dataFromSocket.customerCompanyDataCreated)
      const customerCompanyAlreadyAdded = currentCustomerCompaniesDataRef.current.find((customerCompany) => String(customerCompany._id) === String(customerCompanyDataToAdd._id))

      if (!customerCompanyAlreadyAdded) {
        handleAddCustomerCompanyDataToTable(customerCompanyDataToAdd)
      }
    })

    appDataSocket.on('updateCustomerCompany', async () => {
      const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
      handleGetCustomerCompaniesBriefDataFromRemote(selectedCountry)
    })

    appDataSocket.on('deleteCustomerCompany', async (dataFromSocket: any) => {
      handleDeleteCustomerCompanyFromLocalDB(dataFromSocket.documentKey)
      const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
      handleGetCustomerCompaniesBriefDataFromRemote(selectedCountry)
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDataSocket, isAppDataSocketInitialized])

  const handleGetCustomerCompanyDataFromSocket = (customerCompanyDataCreated: CustomerCompany) => {
    let balance = 0
    const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'

    if (customerCompanyDataCreated.balances && Array.isArray(customerCompanyDataCreated.balances)) {
      const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry]
      const balanceToSetByCurrency = customerCompanyDataCreated.balances.find((balance: { currency: string }) => balance.currency === selectedCurrency.toLowerCase())
      balance = Number(balanceToSetByCurrency?.balance || 0)
    }

    return {
      balance,
      _id: String(customerCompanyDataCreated._id),
      activated: customerCompanyDataCreated.activated,
      businessCompany: {
        name: customerCompanyDataCreated.businessCompany?.name,
        email: customerCompanyDataCreated.businessCompany?.email
      },
      companyMainUserData: {
        email: customerCompanyDataCreated.companyMainUserData?.email
      },
      preferredLanguage: customerCompanyDataCreated.preferredLanguage
    }
  }

  const handleGetCurrentCustomerCompaniesBriefData = async () => {
    let localCustomerCompaniesBriefDataToSet: any = {}
    const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
    const localCurrentCustomerCompaniesBriefData = globalThis.sessionStorage.getItem(`customerCompaniesBriefData:${selectedCountry}`)

    if (localCurrentCustomerCompaniesBriefData) {
      try {
        localCustomerCompaniesBriefDataToSet = JSON.parse(localCurrentCustomerCompaniesBriefData)
        handleSetInitialCustomerCompaniesBriefData(localCustomerCompaniesBriefDataToSet)
      } catch (error) {
        console.log('Error parsing cached customerCompaniesBriefData, refetching from remote', error)
        globalThis.sessionStorage.removeItem(`customerCompaniesBriefData:${selectedCountry}`)
      }
    }

    handleGetCustomerCompaniesBriefDataFromRemote(selectedCountry)
  }

  const handleGetCustomerCompaniesBriefDataFromRemote = async (selectedCountry: string) => {
    try {
      const { data } = await getCurrentCustomerCompaniesBriefData(selectedCountry)

      globalThis.sessionStorage.setItem(`customerCompaniesBriefData:${selectedCountry}`, JSON.stringify(data))
      handleSetInitialCustomerCompaniesBriefData(data)
    } catch (error) {
      console.log('Error handleGetCustomerCompaniesBriefDataFromRemote > ', error)
      currentLoadingDataRef.current = false
      setLoadingLocalData(false)
    }
  }

  const handleGetCurrentBankingPartners = async () => {
    let localCurrentBankingPartnersToSet: any = {}
    const localCurrentBankingPartners = globalThis.sessionStorage.getItem('currentBankingPartners')

    if (localCurrentBankingPartners) {
      localCurrentBankingPartnersToSet = JSON.parse(localCurrentBankingPartners)
      handleSetInitialData(localCurrentBankingPartnersToSet)
    }

    try {
      const { data } = await getCurrentBankingPartners()

      globalThis.sessionStorage.setItem('currentBankingPartners', JSON.stringify(data))
      handleSetInitialData(data)
    } catch (error) {
      console.log('Error handleGetCurrentBankingPartners > ', error)
    }
  }

  const handleSetInitialCustomerCompaniesBriefData = (dataToSet: any) => {
    const customerCompaniesBriefData = dataToSet?.customerCompaniesBriefData

    if (!Array.isArray(customerCompaniesBriefData)) {
      return
    }

    if (Array.isArray(customerCompaniesBriefData) && customerCompaniesBriefData.length === 0 && currentLoadingDataRef.current === true) {
      currentLoadingDataRef.current = false
      currentCustomerCompaniesDataRef.current = []
      setLoadingLocalData(false)
      return
    }

    const currentSelectedCustomerCompaniesIds =
      currentCustomerCompaniesDataRef.current.length === 0
        ? []
        : currentCustomerCompaniesDataRef.current.filter((customerCompanyData) => customerCompanyData.selected === true).map((customerCompanyData) => String(customerCompanyData._id))
    const currentListPageDataState = currentListPageRef.current
    const currentCustomerCompanyToSearchState = customerCompanyToSearchRef.current
    const customerCompaniesNormalizedData = []

    for (const customerCompanyBriefData of customerCompaniesBriefData) {
      const customerCompanyData = { ...customerCompanyBriefData }
      const customerCompanyNormalizedData = normalizeCustomerCompanyData(customerCompanyData) as CustomerCompany

      if (currentSelectedCustomerCompaniesIds.includes(String(customerCompanyNormalizedData._id))) {
        customerCompanyNormalizedData.selected = true
      } else {
        customerCompanyNormalizedData.selected = false
      }

      customerCompaniesNormalizedData.push(customerCompanyNormalizedData)
    }

    if (currentCustomerCompanyToSearchState === '' || currentCustomerCompanyToSearchState === null) {
      const firstPageIndex = currentListPageDataState === null ? 0 : (currentListPageDataState - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const customerCompaniesPerPage = customerCompaniesNormalizedData.slice(firstPageIndex, lastPageIndex)

      handleCustomerCompaniesDeletionProcess(customerCompaniesPerPage) // --> implementar esto
      setCustomerCompaniesList(customerCompaniesPerPage)

      for (const customerCompanyPerPage of customerCompaniesPerPage) {
        const customerCompanyToAdd = normalizeCustomerCompanyData(customerCompanyPerPage)
        handleSyncDataToLocalDB('customer-companies', customerCompanyToAdd)
      }
    }

    currentCustomerCompaniesDataRef.current = customerCompaniesNormalizedData
    setCustomerCompaniesData(customerCompaniesNormalizedData)

    if (currentLoadingDataRef.current === true) {
      currentLoadingDataRef.current = false
      setLoadingLocalData(false)
    }
  }

  const handleSetInitialData = (dataToSet: any) => {
    setAvailableCurrencies(dataToSet.availableCurrencies)
    setCurrentBankingPartners(dataToSet.currentBankingPartners)
  }

  const handleAddCustomerCompanyDataToTable = async (customerCompanyDataToAddToTable: any) => {
    const currentListPageDataState = currentListPageRef.current
    const currentCustomerCompanyToSearchState = customerCompanyToSearchRef.current
    const currentCustomerCompaniesDataState = currentCustomerCompaniesDataRef.current ?? []

    const customerCompanyAlreadyAddedToTable = currentCustomerCompaniesDataState.find((customerCompanyData) => String(customerCompanyData._id) === String(customerCompanyDataToAddToTable._id))

    if (!customerCompanyAlreadyAddedToTable) {
      const customerCompaniesDataToDisplayUpdated = currentCustomerCompaniesDataState.concat(customerCompanyDataToAddToTable)
      currentCustomerCompaniesDataRef.current = customerCompaniesDataToDisplayUpdated
      customerCompaniesDataToDisplayUpdated.sort((thisCustomerCompany: CustomerCompany, nextCustomerCompany: CustomerCompany) => {
        const thisBusinessCompanyName = thisCustomerCompany.businessCompany?.name ? thisCustomerCompany.businessCompany?.name?.toLowerCase() : ''
        const nextBusinessCompanyName = nextCustomerCompany.businessCompany?.name ? nextCustomerCompany.businessCompany?.name?.toLowerCase() : ''

        return thisBusinessCompanyName.localeCompare(nextBusinessCompanyName)
      })

      if (currentCustomerCompanyToSearchState === '' || currentCustomerCompanyToSearchState === null) {
        const firstPageIndex = currentListPageDataState !== null ? (currentListPageDataState - 1) * pageSize : 0
        const lastPageIndex = firstPageIndex + pageSize
        const customerCompaniesPerPage = customerCompaniesDataToDisplayUpdated.slice(firstPageIndex, lastPageIndex)

        handleCustomerCompaniesDeletionProcess(customerCompaniesPerPage)
        setCustomerCompaniesList(customerCompaniesPerPage as Array<CustomerCompany>)
      }

      setCustomerCompaniesData(customerCompaniesDataToDisplayUpdated as Array<CustomerCompany>)
      return customerCompaniesDataToDisplayUpdated
    } else {
      return currentCustomerCompaniesDataState
    }
  }

  const handleDeleteCustomerCompanyFromLocalDB = async (documentKey: string) => {
    const customerCompanyDataSetted: Array<CustomerCompany> = (await handleGetDataFromTableById('customer-companies', String(documentKey))) as Array<CustomerCompany>

    if (customerCompanyDataSetted.length > 0) {
      await handleDeleteDataByOne('customer-companies', documentKey)
    }
  }

  const handleCustomerCompaniesDeletionProcess = (customerCompaniesPerPage: Array<CustomerCompany>) => {
    const currentCustomerCompaniesToDeleteState = (customerCompaniesToDeleteRef.current as Array<string>) ?? []
    const currentShowDeleteButtonFromActionsBarState = currentShowDeleteButtonFromActionsBarRef.current as boolean
    const customerCompaniesToDeleteOutOfPage = []

    for (let i = 0; i < currentCustomerCompaniesToDeleteState.length; i++) {
      const customerCompanyToDeleteId = currentCustomerCompaniesToDeleteState[i]
      const findCustomerCompanyIndex = customerCompaniesPerPage?.findIndex((customerCompanyData) => String(customerCompanyData._id) === String(customerCompanyToDeleteId))

      if (findCustomerCompanyIndex === -1) {
        customerCompaniesToDeleteOutOfPage.push(customerCompanyToDeleteId)
      }
    }

    for (let i = 0; i < customerCompaniesToDeleteOutOfPage.length; i++) {
      const customerCompanyId = customerCompaniesToDeleteOutOfPage[i]
      const index = currentCustomerCompaniesToDeleteState.indexOf(customerCompanyId)
      if (index > -1) {
        currentCustomerCompaniesToDeleteState.splice(index, 1)
      }
    }

    customerCompaniesToDeleteRef.current = currentCustomerCompaniesToDeleteState
    if (customerCompaniesToDeleteRef.current.length < pageSize) {
      setSelectAllCustomerCompanies(false)
    }

    if (customerCompaniesToDeleteRef.current.length === 0) {
      setShowDeleteButtonFromActionsBar(false)
    } else {
      setShowDeleteButtonFromActionsBar(currentShowDeleteButtonFromActionsBarState)
    }

    setTimeout(() => {
      setCustomerCompaniesToDelete(currentCustomerCompaniesToDeleteState)
    }, 50)
  }

  const handleSetCustomerCompanyToSearch = (customerCompanyToSearchData: string, showError: boolean) => {
    currentListPageRef.current = 1
    customerCompanyToSearchRef.current = customerCompanyToSearchData

    setErrors({})
    setCurrentListPage(1)
    setCustomerCompanyToSearch(customerCompanyToSearchData)

    if (customerCompanyToSearchData === '') {
      const firstPageIndex = (currentListPage - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const currentCustomerCompaniesDataState = currentCustomerCompaniesDataRef.current ?? []
      const customerCompaniesPerPage = currentCustomerCompaniesDataState.slice(firstPageIndex, lastPageIndex)

      setCustomerCompaniesList(customerCompaniesPerPage)
      setSearchedCustomerCompanies([])

      if (showError) setErrors({ userToSearch: t('searchField.errors.emptyField', { ns: 'system-users-management' }) })
      return
    }

    const currentFullCustomerCompaniesData = [...(currentCustomerCompaniesDataRef.current as Array<CustomerCompany>)]
    const filteredCustomerCompanies = currentFullCustomerCompaniesData.filter((customerCompany) => {
      const customerCompanyActivationMode = customerCompany.activated === true ? t('clientStatus.active') : t('clientStatus.inactive')
      if (customerCompany.selected === true) customerCompany.selected = false

      return (
        `${customerCompany.businessOwner.firstName} ${customerCompany.businessOwner.lastName}`.toLowerCase().includes(customerCompanyToSearchData.toLowerCase()) ||
        customerCompany.businessOwner.documentNumber.toLowerCase().includes(customerCompanyToSearchData.toLowerCase()) ||
        customerCompany.businessCompany.name.toLowerCase().includes(customerCompanyToSearchData.toLowerCase()) ||
        customerCompany.businessCompany.email.toLowerCase().includes(customerCompanyToSearchData.toLowerCase()) ||
        customerCompany.businessCompany.documentNumber.toLowerCase().includes(customerCompanyToSearchData.toLowerCase()) ||
        customerCompanyActivationMode.toLowerCase().includes(customerCompanyToSearchData.toLowerCase())
      )
    })

    customerCompaniesToDeleteRef.current = []
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const customerCompaniesPerPage = filteredCustomerCompanies.slice(firstPageIndex, lastPageIndex)

    setCustomerCompaniesToDelete([])
    setSelectAllCustomerCompanies(false)
    setCustomerCompaniesList(customerCompaniesPerPage)
    setSearchedCustomerCompanies(filteredCustomerCompanies)
  }

  const handleConfirmCustomerCompanyDeletion = async () => {
    setErrors({})
    setDialogDeleteCustomerCompanyIsLoading(true)
    const currentCustomerCompaniesToDelete = [...customerCompaniesToDelete]

    for (let i = 0; i < currentCustomerCompaniesToDelete.length; i++) {
      const customerCompanyToDeleteId = currentCustomerCompaniesToDelete[i]

      try {
        const accessToken = await resolveAppAccessToken()
        currentShowDeleteButtonFromActionsBarRef.current = false
        setShowDeleteButtonFromActionsBar(false)
        await deleteCustomerCompanyData(customerCompanyToDeleteId, accessToken)
      } catch (error: any) {
        console.log(error, error?.response?.data?.message)
        if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
          handleCloseSession()
        } else if (error?.response?.status === 401) {
          setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
        } else {
          setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
        }

        handleDiscardDeletionProcess()
        return
      }
    }

    handleDiscardDeletionProcess()
    setOpenDeleteCustomerCompanyModal(false)
    updateNewNotificationStatus?.(true, 'customer-company-deleted')
  }

  const handleDiscardDeletionProcess = () => {
    setCustomerCompaniesToDelete([])
    customerCompaniesToDeleteRef.current = []
    setDialogDeleteCustomerCompanyIsLoading(false)
    const currentCustomerCompanyToSearchState = customerCompanyToSearchRef.current
    setCustomerCompanyData(null)

    if (currentCustomerCompanyToSearchState !== '') {
      handleSetCustomerCompanyToSearch('', false)
    }
  }

  const handleToggleGeneralActionChecked = (checked: boolean) => {
    setSelectAllCustomerCompanies(checked)

    if (checked === true) {
      const currentCustomerCompaniesList = [...customerCompaniesList]
      const currentCustomerCompaniesDataStateUpdated = currentCustomerCompaniesDataRef.current
      const customerCompaniesListUpdated = currentCustomerCompaniesList.map((customerCompanyData) => ({
        ...customerCompanyData,
        selected: true
      }))
      const currentCustomerCompaniesDataIds = customerCompaniesListUpdated.map((customerCompanyData) => customerCompanyData._id)
      const filteredCustomerCompaniesToDelete = currentCustomerCompaniesDataIds.filter((id) => id !== undefined) as string[]
      currentShowDeleteButtonFromActionsBarRef.current = true
      customerCompaniesToDeleteRef.current = filteredCustomerCompaniesToDelete

      for (let i = 0; i < customerCompaniesListUpdated.length; i++) {
        const customerCompanyListData = customerCompaniesListUpdated[i]
        const findCustomerCompanyIndex = currentCustomerCompaniesDataStateUpdated?.findIndex((customerCompanyData) => String(customerCompanyData._id) === String(customerCompanyListData._id)) ?? -1

        if (findCustomerCompanyIndex !== -1 && currentCustomerCompaniesDataStateUpdated) {
          currentCustomerCompaniesDataStateUpdated[findCustomerCompanyIndex].selected = true
        }
      }

      currentCustomerCompaniesDataRef.current = currentCustomerCompaniesDataStateUpdated

      setCustomerCompaniesList(customerCompaniesListUpdated)
      setShowDeleteButtonFromActionsBar(true)
      setCustomerCompaniesToDelete(filteredCustomerCompaniesToDelete)
      setCustomerCompaniesData(currentCustomerCompaniesDataStateUpdated as Array<CustomerCompany>)
    } else {
      const currentCustomerCompaniesList = [...customerCompaniesList]
      const currentCustomerCompaniesDataStateUpdated = currentCustomerCompaniesDataRef.current
      const customerCompaniesListUpdated = currentCustomerCompaniesList.map((customerCompanyData) => ({
        ...customerCompanyData,
        selected: false
      }))

      for (let i = 0; i < customerCompaniesListUpdated.length; i++) {
        const customerCompanyListData = customerCompaniesListUpdated[i]
        const findCustomerCompanyIndex = currentCustomerCompaniesDataStateUpdated?.findIndex((customerCompanyData) => String(customerCompanyData._id) === String(customerCompanyListData._id)) ?? -1

        if (findCustomerCompanyIndex !== -1 && currentCustomerCompaniesDataStateUpdated) {
          currentCustomerCompaniesDataStateUpdated[findCustomerCompanyIndex].selected = false
        }
      }

      customerCompaniesToDeleteRef.current = []
      currentShowDeleteButtonFromActionsBarRef.current = false
      currentCustomerCompaniesDataRef.current = currentCustomerCompaniesDataStateUpdated

      setCustomerCompaniesToDelete([])
      setCustomerCompaniesList(customerCompaniesListUpdated)
      setShowDeleteButtonFromActionsBar(false)
      setCustomerCompaniesData(currentCustomerCompaniesDataStateUpdated as Array<CustomerCompany>)
    }
  }

  const handleToggleCustomerCompanySelection = (selectedCustomerCompanyData: CustomerCompany, selected: boolean) => {
    let customerCompaniesSelected = 0
    let customerCompaniesToDeleteUpdated = []
    const currentCustomerCompaniesList = [...customerCompaniesList]
    const currentCustomerCompaniesToDelete = [...customerCompaniesToDelete]
    const currentCustomerCompaniesDataStateUpdated = currentCustomerCompaniesDataRef.current

    const customerCompaniesListUpdated = currentCustomerCompaniesList.map((customerCompanyData) => {
      if (String(customerCompanyData._id) === String(selectedCustomerCompanyData._id)) {
        customerCompanyData.selected = selected
      }

      if (customerCompanyData.selected === true) customerCompaniesSelected++

      return customerCompanyData
    })

    if (selected === true) {
      customerCompaniesToDeleteUpdated = currentCustomerCompaniesToDelete.concat(selectedCustomerCompanyData._id ?? [])
      customerCompaniesToDeleteRef.current = customerCompaniesToDeleteUpdated
      setCustomerCompaniesToDelete(customerCompaniesToDeleteUpdated)

      const findCustomerCompanyIndex = currentCustomerCompaniesDataStateUpdated?.findIndex((customerCompanyData) => String(customerCompanyData._id) === String(selectedCustomerCompanyData._id)) ?? -1

      if (findCustomerCompanyIndex !== -1 && currentCustomerCompaniesDataStateUpdated) {
        currentCustomerCompaniesDataStateUpdated[findCustomerCompanyIndex].selected = true
      }

      currentCustomerCompaniesDataRef.current = currentCustomerCompaniesDataStateUpdated
      setCustomerCompaniesData(currentCustomerCompaniesDataStateUpdated as Array<CustomerCompany>)
    } else {
      const index = currentCustomerCompaniesToDelete.indexOf(selectedCustomerCompanyData._id ?? '')
      if (index > -1) {
        currentCustomerCompaniesToDelete.splice(index, 1)
      }

      customerCompaniesToDeleteUpdated = [...currentCustomerCompaniesToDelete]
      customerCompaniesToDeleteRef.current = customerCompaniesToDeleteUpdated
      setCustomerCompaniesToDelete(currentCustomerCompaniesToDelete)

      const findCustomerCompanyIndex = currentCustomerCompaniesDataStateUpdated?.findIndex((customerCompanyData) => String(customerCompanyData._id) === String(selectedCustomerCompanyData._id)) ?? -1

      if (findCustomerCompanyIndex !== -1 && currentCustomerCompaniesDataStateUpdated) {
        currentCustomerCompaniesDataStateUpdated[findCustomerCompanyIndex].selected = false
      }

      currentCustomerCompaniesDataRef.current = currentCustomerCompaniesDataStateUpdated
      setCustomerCompaniesData(currentCustomerCompaniesDataStateUpdated as Array<CustomerCompany>)
    }

    if (customerCompaniesToDeleteUpdated.length > 0) {
      currentShowDeleteButtonFromActionsBarRef.current = true
      setShowDeleteButtonFromActionsBar(true)
    } else {
      currentShowDeleteButtonFromActionsBarRef.current = false
      setShowDeleteButtonFromActionsBar(false)
    }

    if (customerCompaniesList.length === customerCompaniesSelected) {
      setSelectAllCustomerCompanies(true)
    } else {
      setSelectAllCustomerCompanies(false)
    }

    setCustomerCompaniesList(customerCompaniesListUpdated)
  }

  const handleResendCustomerCompanyInvitation = async (selectedCustomerCompanyData: CustomerCompany) => {
    updateLoadingStatus?.(true)

    try {
      const customerCompanyDataToSend = {
        customerCompanyId: String(selectedCustomerCompanyData._id),
        customerCompanyLanguage: selectedCustomerCompanyData.preferredLanguage,
        customerCompanyEmail: selectedCustomerCompanyData.businessCompany.email || selectedCustomerCompanyData.companyMainUserData.email
      }

      const accessToken = await resolveAppAccessToken()
      await resendCustomerCompanyOnboardingActivationEmail(customerCompanyDataToSend, accessToken)
      // await resendCustomerCompanyActivationEmail(customerCompanyDataToSend, accessTokenData.data.accessToken)

      updateLoadingStatus?.(false)
      updateNewNotificationStatus?.(true, 'resend-email-notification-success')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else {
        updateLoadingStatus?.(false)
        updateNewNotificationStatus?.(true, 'resend-email-notification-error')
      }
    } finally {
      updateLoadingStatus?.(false)
    }
  }

  const handleOpenDeleteCustomerCompanyDataModal = (selectedCustomerCompanyData: CustomerCompany) => {
    const customerCompaniesToDeleteUpdated = [selectedCustomerCompanyData._id] as string[]
    const currentCustomerCompaniesList = [...customerCompaniesList]
    const customerCompaniesListUpdated = currentCustomerCompaniesList.map((customerCompanyData) => ({
      ...customerCompanyData,
      selected: false
    }))

    setCustomerCompanyData({
      ...selectedCustomerCompanyData
    })
    setSelectAllCustomerCompanies(false)
    setOpenDeleteCustomerCompanyModal(true)
    setShowDeleteButtonFromActionsBar(false)
    setCustomerCompaniesList(customerCompaniesListUpdated)
    setCustomerCompaniesToDelete(customerCompaniesToDeleteUpdated)
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <div className="section-container">
      <Breadcrumbs aria-label="breadcrumb">
        <Typography
          key="3"
          className="breadcrumb-active"
        >
          {t('breadcrumbs.firstLevel')}
        </Typography>
      </Breadcrumbs>
      <ActionsBar
        errors={errors}
        userTypeName={t('userTypeName')}
        handleClearSearchInput={() => setErrors({})}
        generalActionChecked={selectAllCustomerCompanies}
        customerCompanyToSearch={customerCompanyToSearch}
        showDeleteButton={showDeleteButtonFromActionsBar}
        handleToggleGeneralActionChecked={handleToggleGeneralActionChecked}
        handleSetCustomerCompanyToSearch={handleSetCustomerCompanyToSearch}
        handleOpenAddClientModal={() => setOpenNewCustomerCompanyModal(true)}
        handleOpenCreateBankingPartnerTestModal={() => setOpenCreateBankingPartnerTestModal(true)}
        customerCompaniesData={customerCompanyToSearch === '' ? customerCompaniesData : searchedCustomerCompanies}
        handleDialogOpenState={(actionToDo: string) => {
          if (actionToDo === 'delete') {
            setOpenDeleteCustomerCompanyModal(!openDeleteCustomerCompanyModal)
            return
          }

          const firstPageIndex = (currentListPage - 1) * pageSize
          const lastPageIndex = firstPageIndex + pageSize
          const customerCompaniesPerPage = customerCompaniesData.slice(firstPageIndex, lastPageIndex)

          setCustomerCompaniesList(customerCompaniesPerPage)
          setOpenNewCustomerCompanyModal(!openNewCustomerCompanyModal)
        }}
      />
      <div className="table-header-container">
        <div className="table-header-text first-table-header table-header-35-width">{t('tableHeaders.name')}</div>
        <div className="table-header-text table-header-30-width text-align-right-large hide-middle">{t('tableHeaders.balance')}</div>
        <div className="table-header-text table-header-35-width hide-large">{t('tableHeaders.email')}</div>
        <div className="table-header-text table-header-12-width"></div>
        {(!userSession?.subRole || (userSession?.subRole && userSession?.subRole !== 'compliance-specialist')) && <div className="table-header-text table-header-12-width">{t('tableHeaders.actions')}</div>}
      </div>
      {loadingLocalData ? (
        <div className="users-management-users-list-container">
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
        </div>
      ) : (
        <>
          <div className="users-management-users-list-container">
            {customerCompaniesList.map((customerCompanyData: CustomerCompany, index: number) => {
              return (
                <CustomerCompanyDataListItem
                  key={index}
                  customerCompanyData={customerCompanyData}
                  handleToggleCustomerCompanySelection={handleToggleCustomerCompanySelection}
                  handleResendCustomerCompanyInvitation={handleResendCustomerCompanyInvitation}
                  handleOpenDeleteCustomerCompanyDataModal={handleOpenDeleteCustomerCompanyDataModal}
                  handleOpenCustomerCompanyFundingDataModal={(selectedCustomerCompanyData: CustomerCompany) => {
                    setCustomerCompanyData(selectedCustomerCompanyData)
                    setOpenNewCustomerCompanyFundingModal(true)
                  }}
                />
              )
            })}
          </div>
          <Pagination
            pageSize={pageSize}
            currentPage={currentListPage}
            handlePaginationPageChange={(page: number) => {
              setScrollToTop(true)
              customerCompaniesToDeleteRef.current = []
              setCustomerCompaniesToDelete([])
              setSelectAllCustomerCompanies(false)
              currentListPageRef.current = page
              setCurrentListPage(page)
            }}
            handleChangePageSize={(pageSizeToSet: string) => {
              setScrollToTop(true)
              setPageSize(Number(pageSizeToSet))
            }}
            totalCount={customerCompanyToSearch === '' ? customerCompaniesData.length : searchedCustomerCompanies.length}
          />
          {openNewCustomerCompanyModal && (
            <AddNewCustomerCompanyModal
              bankingPartnersDataToUpdate={[]}
              handleCloseSession={handleCloseSession}
              availableCurrencies={availableCurrencies}
              currentBankingPartners={currentBankingPartners}
              handleAddCustomerCompanyDataToTable={handleAddCustomerCompanyDataToTable}
              handleOpenStateOfModal={(openState: boolean) => setOpenNewCustomerCompanyModal(openState)}
            />
          )}
          {openNewCustomerCompanyFundingModal && (
            <AddNewCustomerCompanyFundingModal
              handleCloseSession={handleCloseSession}
              customerCompanyData={customerCompanyData}
              handleOpenStateOfModal={(openState: boolean) => {
                if (openState === false) {
                  setCustomerCompanyData(null)
                }

                setOpenNewCustomerCompanyFundingModal(openState)
              }}
            />
          )}
          {openDeleteCustomerCompanyModal && (
            <Dialog
              errors={errors}
              actionButtonWarningStyle
              dialogIsLoading={dialogDeleteCustomerCompanyIsLoading}
              handleDialogAction={handleConfirmCustomerCompanyDeletion}
              actionButtonText={t('deleteCustomerCompanyModal.actionButtonText')}
              dialogTitle={customerCompaniesToDelete.length === 1 ? t('deleteCustomerCompanyModal.title') : `${t('deleteCustomerCompanyModal.title')}s`}
              handleDialogOpenState={(openState: boolean) => {
                setOpenDeleteCustomerCompanyModal(openState)

                if (openState === false) {
                  setErrors({})
                  setCustomerCompanyData(null)
                }
              }}
            >
              <div className="dialog-content-text">
                {customerCompanyData ? t('deleteCustomerCompanyModal.simpleUserText', { businessName: customerCompanyData.businessCompany.name.toUpperCase() }) : t('deleteCustomerCompanyModal.multipleUseText')}
              </div>
            </Dialog>
          )}
          {openCreateBankingPartnerTestModal && (
            <Dialog
              maxHeight="865px"
              actionButtonText="Enviar pago"
              actionButtonDisabled={!readyToSend}
              dialogTitle="Enviar pago para probar los rieles"
              dialogIsLoading={createBankingPartnerTestModalIsLoading}
              handleDialogAction={bankingPartnersPaymentsTestFormRef.current?.handleSendPayment}
              handleDialogOpenState={(openState: boolean) => setOpenCreateBankingPartnerTestModal(openState)}
            >
              <BankingPartnersPaymentsTestForm
                customerData={customerData}
                setCustomerData={setCustomerData}
                handleCloseSession={handleCloseSession}
                ref={bankingPartnersPaymentsTestFormRef}
                handleEnableActionButton={(enableActionButton: boolean) => setReadyToSend(enableActionButton)}
                handleIsLoading={(loadingState: boolean) => setCreateBankingPartnerTestModalIsLoading(loadingState)}
              />
            </Dialog>
          )}
        </>
      )}
    </div>
  )
}

export default CustomerCompaniesManagement
